import {
  DEFAULT_ACTIVATION_ACK_TIMEOUT_MS,
  DEFAULT_CONTROLLER_CHANGE_TIMEOUT_MS,
  IDLE_SERVICE_WORKER_UPDATE,
  createServiceWorkerUpdateSnapshot,
  type ServiceWorkerUpdateCode,
  type ServiceWorkerUpdateSnapshot,
  type ServiceWorkerUpdateSource,
} from "./service-worker-update-model.ts";
import {
  createServiceWorkerUpdateRequestId,
  requestServiceWorkerActivation,
  type ServiceWorkerActivationProtocolOptions,
  type ServiceWorkerActivationProtocolResult,
} from "./service-worker-update-protocol.ts";

export type ServiceWorkerUpdateBlocker = () => boolean;

export interface ServiceWorkerUpdateBlockerOptions {
  readonly focus?: () => void;
}

export type ServiceWorkerUpdateApplyResult =
  | "accepted"
  | "blocked"
  | "reloading"
  | "unavailable"
  | "failed"
  | "superseded";

type ActivationRequest = (
  worker: ServiceWorker,
  options: ServiceWorkerActivationProtocolOptions,
) => Promise<ServiceWorkerActivationProtocolResult>;

export interface ServiceWorkerUpdateManagerOptions {
  readonly serviceWorker?: ServiceWorkerContainer | null;
  readonly register?: (
    serviceWorker: ServiceWorkerContainer,
    scriptUrl: string,
    options: RegistrationOptions,
  ) => Promise<ServiceWorkerRegistration>;
  readonly reload?: () => void;
  readonly isOnline?: () => boolean;
  readonly createMessageChannel?: () => MessageChannel;
  readonly createRequestId?: () => string;
  readonly activationRequest?: ActivationRequest;
  readonly activationAckTimeoutMs?: number;
  readonly controllerChangeTimeoutMs?: number;
  readonly scheduleTimeout?: (
    callback: () => void,
    timeoutMs: number,
  ) => ReturnType<typeof setTimeout>;
  readonly cancelTimeout?: (timeoutId: ReturnType<typeof setTimeout>) => void;
}

export interface ServiceWorkerUpdateManager {
  readonly getSnapshot: () => ServiceWorkerUpdateSnapshot;
  subscribe(listener: () => void): () => void;
  start(): Promise<ServiceWorkerUpdateSnapshot>;
  checkForUpdate(): Promise<"checked" | "unavailable" | "failed">;
  defer(): void;
  requestActivation(): Promise<ServiceWorkerUpdateApplyResult>;
  reloadPortal(): ServiceWorkerUpdateApplyResult;
  focusFirstBlocker(): boolean;
  registerBlocker(
    blockerId: string,
    blocker: ServiceWorkerUpdateBlocker,
    options?: ServiceWorkerUpdateBlockerOptions,
  ): () => void;
  stop(): void;
  dispose(): void;
}

interface BlockerEntry {
  readonly isBlocked: ServiceWorkerUpdateBlocker;
  readonly focus?: () => void;
}

interface ActivationIntent {
  readonly requestId: string;
  readonly abortController: AbortController;
}

const BLOCKER_ID_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;

function resolveServiceWorkerContainer(): ServiceWorkerContainer | null {
  try {
    return typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? navigator.serviceWorker
      : null;
  } catch {
    return null;
  }
}

function reloadCurrentPage(): void {
  window.location.reload();
}

function readOnlineState(): boolean {
  try {
    return typeof navigator === "undefined" || navigator.onLine !== false;
  } catch {
    return false;
  }
}

function normalizeTimeout(
  timeoutMs: number | undefined,
  defaultTimeoutMs: number,
): number {
  if (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs)) {
    return defaultTimeoutMs;
  }

  return Math.max(1, Math.min(timeoutMs, defaultTimeoutMs));
}

function snapshotsMatch(
  first: ServiceWorkerUpdateSnapshot,
  second: ServiceWorkerUpdateSnapshot,
): boolean {
  return (
    first.status === second.status &&
    first.action === second.action &&
    first.code === second.code &&
    first.blockerCount === second.blockerCount &&
    first.canFocusBlocker === second.canFocusBlocker &&
    first.source === second.source
  );
}

export function createServiceWorkerUpdateManager(
  options: ServiceWorkerUpdateManagerOptions = {},
): ServiceWorkerUpdateManager {
  const listeners = new Set<() => void>();
  const blockers = new Map<string, Map<symbol, BlockerEntry>>();
  const observedWorkers = new WeakSet<ServiceWorker>();
  const listenerDisposers: Array<() => void> = [];
  const scheduleTimeout = options.scheduleTimeout ?? setTimeout;
  const cancelTimeout = options.cancelTimeout ?? clearTimeout;
  const activationRequest =
    options.activationRequest ?? requestServiceWorkerActivation;
  const activationAckTimeoutMs = normalizeTimeout(
    options.activationAckTimeoutMs,
    DEFAULT_ACTIVATION_ACK_TIMEOUT_MS,
  );
  const controllerChangeTimeoutMs = normalizeTimeout(
    options.controllerChangeTimeoutMs,
    DEFAULT_CONTROLLER_CHANGE_TIMEOUT_MS,
  );

  let snapshot = IDLE_SERVICE_WORKER_UPDATE;
  let serviceWorker: ServiceWorkerContainer | null = null;
  let registration: ServiceWorkerRegistration | null = null;
  let waitingWorker: ServiceWorker | null = null;
  let lastController: ServiceWorker | null = null;
  let startPromise: Promise<ServiceWorkerUpdateSnapshot> | null = null;
  let registrationPromise: Promise<ServiceWorkerRegistration | null> | null =
    null;
  let activationPromise: Promise<ServiceWorkerUpdateApplyResult> | null = null;
  let activationIntent: ActivationIntent | null = null;
  let controllerTimer: ReturnType<typeof setTimeout> | null = null;
  let reloadStarted = false;
  let disposed = false;

  const setSnapshot = (next: ServiceWorkerUpdateSnapshot) => {
    if (snapshotsMatch(snapshot, next)) {
      return;
    }

    snapshot = next;
    for (const listener of listeners) {
      listener();
    }
  };

  const clearControllerTimer = () => {
    if (controllerTimer === null) {
      return;
    }

    try {
      cancelTimeout(controllerTimer);
    } catch {
      // Timer cleanup cannot change the public update state.
    }
    controllerTimer = null;
  };

  const getActiveBlockers = (): BlockerEntry[] => {
    const active: BlockerEntry[] = [];
    for (const entries of blockers.values()) {
      for (const entry of entries.values()) {
        try {
          if (entry.isBlocked()) {
            active.push(entry);
          }
        } catch {
          active.push(entry);
        }
      }
    }
    return active;
  };

  const isOnline = (): boolean => {
    try {
      return (options.isOnline ?? readOnlineState)();
    } catch {
      return false;
    }
  };

  const publishGuardedState = (
    status: "blocked" | "refresh-required",
    code: "UNSAVED_WORK" | "OFFLINE",
    source: ServiceWorkerUpdateSource | null,
    activeBlockers: BlockerEntry[],
  ) => {
    const canFocusBlocker = activeBlockers.some(
      ({ focus }) => typeof focus === "function",
    );
    setSnapshot(
      createServiceWorkerUpdateSnapshot(status, {
        action: code === "UNSAVED_WORK" && canFocusBlocker ? "review" : "retry",
        code,
        blockerCount: activeBlockers.length,
        canFocusBlocker,
        source,
      }),
    );
  };

  const guardAction = (
    status: "blocked" | "refresh-required",
    source: ServiceWorkerUpdateSource | null,
  ): boolean => {
    const activeBlockers = getActiveBlockers();
    if (activeBlockers.length > 0) {
      publishGuardedState(status, "UNSAVED_WORK", source, activeBlockers);
      return false;
    }

    if (!isOnline()) {
      publishGuardedState(status, "OFFLINE", source, activeBlockers);
      return false;
    }

    return true;
  };

  const reloadWhenSafe = (
    source: ServiceWorkerUpdateSource,
  ): ServiceWorkerUpdateApplyResult => {
    if (reloadStarted) {
      return "reloading";
    }

    if (!guardAction("refresh-required", source)) {
      return "blocked";
    }

    reloadStarted = true;
    try {
      (options.reload ?? reloadCurrentPage)();
      return "reloading";
    } catch {
      reloadStarted = false;
      setSnapshot(
        createServiceWorkerUpdateSnapshot("refresh-required", {
          action: "reload",
          code: "RELOAD_FAILED",
          source,
        }),
      );
      return "failed";
    }
  };

  const handleControllerChange = () => {
    if (!serviceWorker || disposed) {
      return;
    }

    const previousController = lastController;
    const nextController = serviceWorker.controller;
    lastController = nextController;
    if (
      !previousController ||
      !nextController ||
      previousController === nextController
    ) {
      return;
    }

    waitingWorker = null;
    if (activationIntent) {
      const { abortController } = activationIntent;
      activationIntent = null;
      clearControllerTimer();
      abortController.abort();
      reloadWhenSafe("controllerchange");
      return;
    }

    setSnapshot(
      createServiceWorkerUpdateSnapshot("refresh-required", {
        action: "reload",
        source: "controllerchange",
      }),
    );
  };

  const publishWaitingWorker = (
    worker: ServiceWorker,
    source: Exclude<ServiceWorkerUpdateSource, "controllerchange">,
  ) => {
    if (
      !serviceWorker?.controller ||
      disposed ||
      activationIntent ||
      snapshot.status === "refresh-required"
    ) {
      return;
    }

    waitingWorker = registration?.waiting ?? worker;
    setSnapshot(
      createServiceWorkerUpdateSnapshot("ready", {
        action: "activate",
        source,
      }),
    );
  };

  const observeInstallingWorker = (
    worker: ServiceWorker | null,
    source: Exclude<ServiceWorkerUpdateSource, "controllerchange">,
  ) => {
    if (!worker) {
      return;
    }

    const hadControllerWhenInstallationStarted =
      serviceWorker?.controller !== null;
    if (observedWorkers.has(worker)) {
      if (
        worker.state === "installing" &&
        hadControllerWhenInstallationStarted &&
        snapshot.status === "checking"
      ) {
        setSnapshot(
          createServiceWorkerUpdateSnapshot("installing", { source }),
        );
      }
      return;
    }

    observedWorkers.add(worker);

    if (
      hadControllerWhenInstallationStarted &&
      !activationIntent &&
      snapshot.status !== "refresh-required"
    ) {
      setSnapshot(createServiceWorkerUpdateSnapshot("installing", { source }));
    }

    const handleStateChange = () => {
      if (disposed) {
        return;
      }

      if (worker.state === "installed") {
        if (hadControllerWhenInstallationStarted) {
          publishWaitingWorker(worker, source);
        } else if (snapshot.status === "registering") {
          setSnapshot(IDLE_SERVICE_WORKER_UPDATE);
        }
        return;
      }

      if (
        worker.state === "redundant" &&
        hadControllerWhenInstallationStarted &&
        !registration?.waiting &&
        !activationIntent &&
        snapshot.status !== "refresh-required"
      ) {
        setSnapshot(
          createServiceWorkerUpdateSnapshot("error", {
            action: "retry",
            code: "UPDATE_INSTALL_FAILED",
            source,
          }),
        );
      }
    };

    worker.addEventListener("statechange", handleStateChange);
    listenerDisposers.push(() => {
      worker.removeEventListener("statechange", handleStateChange);
    });
    handleStateChange();
  };

  const attachRegistration = (nextRegistration: ServiceWorkerRegistration) => {
    registration = nextRegistration;
    const handleUpdateFound = () => {
      observeInstallingWorker(nextRegistration.installing, "updatefound");
    };

    nextRegistration.addEventListener("updatefound", handleUpdateFound);
    listenerDisposers.push(() => {
      nextRegistration.removeEventListener("updatefound", handleUpdateFound);
    });

    if (nextRegistration.waiting && serviceWorker?.controller) {
      publishWaitingWorker(nextRegistration.waiting, "existing-waiting");
      return;
    }

    observeInstallingWorker(nextRegistration.installing, "updatefound");
    if (snapshot.status === "registering") {
      setSnapshot(IDLE_SERVICE_WORKER_UPDATE);
    }
  };

  const ensureRegistration = (): Promise<ServiceWorkerRegistration | null> => {
    if (registration) {
      return Promise.resolve(registration);
    }
    if (registrationPromise) {
      return registrationPromise;
    }
    if (!serviceWorker) {
      return Promise.resolve(null);
    }

    setSnapshot(createServiceWorkerUpdateSnapshot("registering"));
    registrationPromise = (async () => {
      try {
        const nextRegistration = options.register
          ? await options.register(serviceWorker!, "/sw.js", {
              scope: "/",
              updateViaCache: "none",
            })
          : await serviceWorker!.register("/sw.js", {
              scope: "/",
              updateViaCache: "none",
            });

        if (!disposed) {
          attachRegistration(nextRegistration);
          return nextRegistration;
        }
        return null;
      } catch {
        if (
          !disposed &&
          !["activating", "refresh-required"].includes(snapshot.status)
        ) {
          setSnapshot(
            createServiceWorkerUpdateSnapshot("error", {
              action: "retry",
              code: "REGISTRATION_FAILED",
            }),
          );
        }
        return null;
      } finally {
        registrationPromise = null;
      }
    })();

    return registrationPromise;
  };

  const start = (): Promise<ServiceWorkerUpdateSnapshot> => {
    if (startPromise) {
      return startPromise;
    }

    startPromise = (async () => {
      serviceWorker =
        "serviceWorker" in options
          ? (options.serviceWorker ?? null)
          : resolveServiceWorkerContainer();

      if (!serviceWorker) {
        setSnapshot(createServiceWorkerUpdateSnapshot("unsupported"));
        return snapshot;
      }

      lastController = serviceWorker.controller;
      serviceWorker.addEventListener(
        "controllerchange",
        handleControllerChange,
      );
      listenerDisposers.push(() => {
        serviceWorker?.removeEventListener(
          "controllerchange",
          handleControllerChange,
        );
      });

      await ensureRegistration();
      return snapshot;
    })();

    return startPromise;
  };

  const requestActivation = (): Promise<ServiceWorkerUpdateApplyResult> => {
    if (activationPromise) {
      return activationPromise;
    }
    if (activationIntent) {
      return Promise.resolve("accepted");
    }

    const run = async (): Promise<ServiceWorkerUpdateApplyResult> => {
      const worker = registration?.waiting ?? waitingWorker;
      if (!worker || worker.state !== "installed") {
        waitingWorker = null;
        setSnapshot(IDLE_SERVICE_WORKER_UPDATE);
        return "unavailable";
      }

      if (!guardAction("blocked", snapshot.source)) {
        return "blocked";
      }

      const requestId = (
        options.createRequestId ?? createServiceWorkerUpdateRequestId
      )();
      const abortController = new AbortController();
      activationIntent = { requestId, abortController };
      setSnapshot(
        createServiceWorkerUpdateSnapshot("activating", {
          source: snapshot.source,
        }),
      );

      clearControllerTimer();
      controllerTimer = scheduleTimeout(() => {
        controllerTimer = null;
        if (disposed || activationIntent?.requestId !== requestId) {
          return;
        }

        activationIntent = null;
        abortController.abort();
        setSnapshot(
          createServiceWorkerUpdateSnapshot("error", {
            action: "retry",
            code: "ACTIVATION_TIMEOUT",
            source: snapshot.source,
          }),
        );
      }, controllerChangeTimeoutMs);

      let result: ServiceWorkerActivationProtocolResult;
      try {
        result = await activationRequest(worker, {
          requestId,
          timeoutMs: activationAckTimeoutMs,
          signal: abortController.signal,
          scheduleTimeout,
          cancelTimeout,
          ...(options.createMessageChannel
            ? { createMessageChannel: options.createMessageChannel }
            : {}),
        });
      } catch {
        result = {
          status: "failed",
          requestId,
          code: "ACTIVATION_POST_FAILED",
        };
      }

      if (disposed || activationIntent?.requestId !== requestId) {
        return "superseded";
      }

      if (result.status === "accepted") {
        return "accepted";
      }

      activationIntent = null;
      clearControllerTimer();
      if (result.code === "ACTIVATION_CANCELLED") {
        return "superseded";
      }

      setSnapshot(
        createServiceWorkerUpdateSnapshot("error", {
          action: "retry",
          code: result.code as ServiceWorkerUpdateCode,
          source: snapshot.source,
        }),
      );
      return "failed";
    };

    activationPromise = run().finally(() => {
      activationPromise = null;
    });
    return activationPromise;
  };

  const stop = () => {
    if (disposed) {
      return;
    }

    disposed = true;
    clearControllerTimer();
    activationIntent?.abortController.abort();
    activationIntent = null;
    for (const disposeListener of listenerDisposers.splice(0)) {
      disposeListener();
    }
    blockers.clear();
    listeners.clear();
    registration = null;
    waitingWorker = null;
    serviceWorker = null;
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start,
    async checkForUpdate() {
      if (["activating", "refresh-required"].includes(snapshot.status)) {
        return "unavailable";
      }
      if (!startPromise) {
        await start();
      }
      if (!serviceWorker) {
        return "unavailable";
      }

      const currentRegistration = registration ?? (await ensureRegistration());
      if (!currentRegistration) {
        return "failed";
      }
      if (["activating", "refresh-required"].includes(snapshot.status)) {
        return "unavailable";
      }

      setSnapshot(createServiceWorkerUpdateSnapshot("checking"));
      try {
        await currentRegistration.update();
        if (["activating", "refresh-required"].includes(snapshot.status)) {
          return "checked";
        }

        if (currentRegistration.waiting) {
          publishWaitingWorker(currentRegistration.waiting, "existing-waiting");
        } else if (currentRegistration.installing) {
          observeInstallingWorker(
            currentRegistration.installing,
            "updatefound",
          );
        } else if (
          !["ready", "deferred", "blocked"].includes(snapshot.status)
        ) {
          setSnapshot(IDLE_SERVICE_WORKER_UPDATE);
        }
        return "checked";
      } catch {
        if (
          ![
            "ready",
            "deferred",
            "blocked",
            "activating",
            "refresh-required",
          ].includes(snapshot.status)
        ) {
          setSnapshot(
            createServiceWorkerUpdateSnapshot("error", {
              action: "retry",
              code: "UPDATE_CHECK_FAILED",
            }),
          );
        }
        return "failed";
      }
    },
    defer() {
      if (
        snapshot.status === "ready" ||
        (snapshot.status === "blocked" && waitingWorker)
      ) {
        setSnapshot(
          createServiceWorkerUpdateSnapshot("deferred", {
            source: snapshot.source,
          }),
        );
      }
    },
    requestActivation,
    reloadPortal() {
      if (snapshot.status !== "refresh-required") {
        return "unavailable";
      }
      return reloadWhenSafe("controllerchange");
    },
    focusFirstBlocker() {
      for (const entry of getActiveBlockers()) {
        if (!entry.focus) {
          continue;
        }
        try {
          entry.focus();
          return true;
        } catch {
          // A stale focus target must not expose feature details or stop review.
        }
      }
      return false;
    },
    registerBlocker(blockerId, blocker, blockerOptions = {}) {
      if (!BLOCKER_ID_PATTERN.test(blockerId)) {
        throw new Error(
          "Service-worker update blocker IDs must be static safe names.",
        );
      }

      const token = Symbol(blockerId);
      const entries = blockers.get(blockerId) ?? new Map();
      entries.set(token, {
        isBlocked: blocker,
        focus: blockerOptions.focus,
      });
      blockers.set(blockerId, entries);

      return () => {
        const registeredEntries = blockers.get(blockerId);
        registeredEntries?.delete(token);
        if (registeredEntries?.size === 0) {
          blockers.delete(blockerId);
        }

        if (
          snapshot.code === "UNSAVED_WORK" &&
          ["blocked", "refresh-required"].includes(snapshot.status)
        ) {
          const activeBlockers = getActiveBlockers();
          if (activeBlockers.length === 0) {
            setSnapshot(
              snapshot.status === "refresh-required"
                ? createServiceWorkerUpdateSnapshot("refresh-required", {
                    action: "reload",
                    source: snapshot.source,
                  })
                : waitingWorker
                  ? createServiceWorkerUpdateSnapshot("ready", {
                      action: "activate",
                      source: snapshot.source,
                    })
                  : IDLE_SERVICE_WORKER_UPDATE,
            );
            return;
          }

          setSnapshot(
            createServiceWorkerUpdateSnapshot(snapshot.status, {
              action: activeBlockers.some(({ focus }) => focus)
                ? "review"
                : "retry",
              code: snapshot.code,
              blockerCount: activeBlockers.length,
              canFocusBlocker: activeBlockers.some(({ focus }) => focus),
              source: snapshot.source,
            }),
          );
        }
      };
    },
    stop,
    dispose: stop,
  };
}

export const portalServiceWorkerUpdates = createServiceWorkerUpdateManager();

export function registerPortalServiceWorkerUpdateBlocker(
  blockerId: string,
  blocker: ServiceWorkerUpdateBlocker,
  options?: ServiceWorkerUpdateBlockerOptions,
): () => void {
  return portalServiceWorkerUpdates.registerBlocker(
    blockerId,
    blocker,
    options,
  );
}
